import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { Table, Alert, Spinner } from 'react-bootstrap';
import './TestMarksPrint.css';

const getAuthToken = () => localStorage.getItem('authToken');

const CompareTestMarks = () => {
  const location = useLocation();
  const printRef = useRef();

  const { batchId, testIds } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      batchId: query.get('batch'),
      testIds: query.get('tests')?.split(',').filter(Boolean) || [],
    };
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [students, setStudents] = useState([]);
  const [tests, setTests] = useState([]);
  const [marksMap, setMarksMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const res = await axios.post(
          `${appConstants.API_ROOT_URL}compare-test-marks`,
          {
            batch_id: batchId,
            test_ids: testIds,
          },
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          },
        );

        const { students = [], tests = [], marks = [] } = res.data.data || {};

        const map = {};

        students.forEach((stu) => {
          map[stu.id] = {
            name: stu.name,
            marks: {},
          };
        });

        marks.forEach((mark) => {
          const studentId = mark.student_id;
          const testIdStr = String(mark.test_id); // normalize to string
          const test = tests.find((t) => String(t.id) === testIdStr);

          if (map[studentId] && test) {
            map[studentId].marks[test.id] = mark.mark ?? null;
          }
        });

        setStudents(students);
        setTests(tests);
        setMarksMap(map);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(
          'Failed to load test comparison data. Please try again later.',
        );
      }

      setLoading(false);
    };

    if (batchId && testIds.length > 0) {
      fetchData();
    } else {
      setError('Missing batch or test IDs in the query.');
      setLoading(false);
    }
  }, [batchId, testIds]);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Compare Student Marks</h4>
        <button
          className="btn btn-outline-primary"
          onClick={() => window.print()}
        >
          Print / Save as PDF
        </button>
      </div>

      <div className="container mt-1" ref={printRef}>
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Loading...</div>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : students.length === 0 ? (
          <Alert variant="info">No students found in this batch.</Alert>
        ) : tests.length === 0 ? (
          <Alert variant="info">No tests found to compare.</Alert>
        ) : (
          <div className="table-responsive" id="print-area">
            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  <th className="fixed-name-col">Student</th>
                  {tests.map((test) => (
                    <th key={test.id}>
                      {test.name} ({test.subject_name})
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((stu) => (
                  <tr key={stu.id}>
                    <td className="fixed-name-col">{stu.name}</td>
                    {tests.map((test) => {
                      const mark = marksMap[stu.id]?.marks?.[test.id];
                      return <td key={test.id}>{mark != null ? mark : '-'}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CompareTestMarks;
